import { Component } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { portalCommands } from 'src/app/utils/portal-path';
import * as QRCode from 'qrcode';
import { ParentsService } from 'src/app/services/parents.service';
import { StudentsService } from 'src/app/services/students.service';
import { ClassesService } from 'src/app/services/classes.service';
import { LanguageService } from 'src/app/services/language.service';
import { TranslateService } from '@ngx-translate/core';
import { SchoolSettingsService } from 'src/app/services/school-settings.service';
import { ParentsListPdfService } from 'src/app/services/parents-list-pdf.service';
import { ParentCardsPdfService } from 'src/app/services/parent-cards-pdf.service';
import { SearchableSelectOption } from 'src/app/shared/searchable-select/searchable-select.component';
import { DEFAULT_STUDENT_CARD_COLORS } from 'src/app/services/school-settings.service';

type ParentsPrintScope = 'all' | 'no_bus';
type ParentsCardsScope = 'all' | 'primary';
type ParentsStatsFilter = 'all' | 'multi' | 'bus' | 'no_bus';

@Component({
  selector: 'app-parents',
  templateUrl: './parents.component.html',
  styleUrls: ['./parents.component.css']
})
export class ParentsComponent {
  items: any[] = [];
  filteredItems: any[] = [];
  students: any[] = [];
  classes: any[] = [];
  selected: any[] = [];
  searchTerm = '';
  selectedClassId = '';
  statsFilter: ParentsStatsFilter = 'all';
  pageSize = 10;
  pageIndex = 0;
  pageSizeOptions = [10, 20, 50];
  showDeleteModal = false;
  deleteModalBody = '';
  deleteModalParams: Record<string, unknown> = {};
  open = false;
  mode = 'add';
  selectedId = '';
  isError = false;
  errorMessage = '';
  contactLabels = ['mobile', 'home', 'work'];
  readonly relations = ['father', 'mother', 'guardian'];
  qrCode = '';
  qrDataUrl = '';
  printScope: ParentsPrintScope = 'all';
  cardsParentScope: ParentsCardsScope = 'all';
  exportingList = false;
  exportingCards = false;
  private schoolName = '';
  private cutMarginMm = DEFAULT_STUDENT_CARD_COLORS.cutMarginMm;

  form = new FormGroup({
    firstNameAr: new FormControl('', [Validators.required]),
    lastNameAr: new FormControl('', [Validators.required]),
    firstNameEn: new FormControl('', [Validators.required]),
    lastNameEn: new FormControl('', [Validators.required]),
    phones: new FormArray([]),
    emails: new FormArray([]),
    studentLinks: new FormArray([]),
  });

  constructor(
    private parentsService: ParentsService,
    private studentsService: StudentsService,
    private classesService: ClassesService,
    private languageService: LanguageService,
    private translate: TranslateService,
    private schoolSettingsService: SchoolSettingsService,
    private parentsListPdfService: ParentsListPdfService,
    private parentCardsPdfService: ParentCardsPdfService,
    private router: Router,
    private route: ActivatedRoute,
  ) {}

  get phones(): FormArray { return this.form.get('phones') as FormArray; }
  get emails(): FormArray { return this.form.get('emails') as FormArray; }
  get studentLinks(): FormArray { return this.form.get('studentLinks') as FormArray; }

  get studentOptions(): SearchableSelectOption[] {
    return this.students.map(student => ({
      value: student.id,
      label: this.displayPersonName(student),
      searchText: `${student.firstNameAr || ''} ${student.lastNameAr || ''} ${student.firstNameEn || ''} ${student.lastNameEn || ''} ${student.studentCode || ''}`,
    }));
  }

  get relationOptions(): SearchableSelectOption[] {
    return this.relations.map(relation => ({
      value: relation,
      label: this.translate.instant(`STUDENTS.RELATION_${relation.toUpperCase()}`),
    }));
  }

  get printScopeOptions(): SearchableSelectOption[] {
    return [
      {
        value: 'all',
        label: this.translate.instant('PARENTS.PRINT_SCOPE_ALL'),
      },
      {
        value: 'no_bus',
        label: this.translate.instant('PARENTS.PRINT_SCOPE_NO_BUS'),
      },
    ];
  }

  get cardsParentScopeOptions(): SearchableSelectOption[] {
    return [
      {
        value: 'all',
        label: this.translate.instant('PARENTS.CARDS_SCOPE_ALL'),
      },
      {
        value: 'primary',
        label: this.translate.instant('PARENTS.CARDS_SCOPE_PRIMARY'),
      },
    ];
  }

  get allClassesLabel(): string {
    return this.translate.instant('STUDENTS.ALL_CLASSES');
  }

  get classOptions(): SearchableSelectOption[] {
    return this.classes.map(cls => ({
      value: String(cls.id || cls._id || ''),
      label: cls.major?.name ? `${cls.name} — ${cls.major.name}` : (cls.name || ''),
      searchText: `${cls.name || ''} ${cls.major?.name || ''}`,
    })).filter(option => option.value);
  }

  get exporting(): boolean {
    return this.exportingList || this.exportingCards;
  }

  /** Parents after class filter only — used for summary counts. */
  get classScopedItems(): any[] {
    if (!this.selectedClassId) {
      return this.items;
    }
    return this.items.filter(item => this.parentHasStudentInClass(item, this.selectedClassId));
  }

  get statsAllCount(): number {
    return this.classScopedItems.length;
  }

  get statsMultiCount(): number {
    return this.classScopedItems.filter(item => this.scopedStudents(item).length > 1).length;
  }

  get statsBusCount(): number {
    return this.classScopedItems.filter(item =>
      this.scopedStudents(item).some((student: any) => !this.studentHasNoBus(student)),
    ).length;
  }

  get statsNoBusCount(): number {
    return this.classScopedItems.filter(item =>
      this.scopedStudents(item).some((student: any) => this.studentHasNoBus(student)),
    ).length;
  }

  get searchPlaceholder(): string {
    return this.translate.instant('COMMON.SEARCH');
  }

  async ngOnInit() {
    const [_, settings, classes] = await Promise.all([
      Promise.all([this.fetchItems(), this.loadStudents()]),
      this.schoolSettingsService.get().catch(() => null),
      this.classesService.list().catch(() => []),
    ]);
    this.schoolName = settings?.schoolName || '';
    this.cutMarginMm = settings?.studentCardColors?.cutMarginMm
      ?? DEFAULT_STUDENT_CARD_COLORS.cutMarginMm;
    this.classes = (classes || [])
      .slice()
      .sort((a: any, b: any) => String(a.name || '').localeCompare(String(b.name || ''), 'ar'));
    const editId = this.route.snapshot.queryParamMap.get('edit');
    if (editId) {
      await this.openEditById(editId);
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { edit: null },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
    }
  }

  displayPersonName(item: any): string {
    const isAr = this.languageService.current === 'ar';
    const name = isAr
      ? `${item.firstNameAr || ''} ${item.lastNameAr || ''}`.trim()
      : `${item.firstNameEn || ''} ${item.lastNameEn || ''}`.trim();
    if (name) {
      return item.studentCode ? `${name} (${item.studentCode})` : name;
    }
    return item.studentCode || '—';
  }

  displayName(item: any): string {
    return this.displayPersonName(item);
  }

  studentsSummary(item: any): string {
    const linked = item?.students || [];
    if (!linked.length) {
      return '—';
    }
    return linked.map((student: any) => this.displayPersonName(student)).join(', ');
  }

  async loadStudents() {
    this.students = await this.studentsService.list();
  }

  async fetchItems() {
    this.items = await this.parentsService.list();
    this.applyFilter();
  }

  get paginatedItems() {
    const start = this.pageIndex * this.pageSize;
    return this.filteredItems.slice(start, start + this.pageSize);
  }

  get totalPages() {
    return Math.max(1, Math.ceil(this.filteredItems.length / this.pageSize));
  }

  get pageStart() {
    return this.filteredItems.length ? this.pageIndex * this.pageSize + 1 : 0;
  }

  get pageEnd() {
    return Math.min((this.pageIndex + 1) * this.pageSize, this.filteredItems.length);
  }

  applyFilter() {
    const term = this.searchTerm.trim().toLowerCase();
    this.filteredItems = this.classScopedItems.filter(item => {
      if (!this.matchesStatsFilter(item)) {
        return false;
      }
      if (!term) {
        return true;
      }
      const haystack = `${this.displayName(item)} ${this.studentsSummary(item)}`.toLowerCase();
      return haystack.includes(term);
    });
    this.pageIndex = 0;
  }

  onSearchChange() { this.applyFilter(); }

  onClassChange() {
    this.clearSelection();
    this.applyFilter();
  }

  setStatsFilter(filter: ParentsStatsFilter) {
    this.statsFilter = filter;
    this.clearSelection();
    this.applyFilter();
  }

  private matchesStatsFilter(item: any): boolean {
    const students = this.scopedStudents(item);
    switch (this.statsFilter) {
      case 'multi':
        return students.length > 1;
      case 'bus':
        return students.some((student: any) => !this.studentHasNoBus(student));
      case 'no_bus':
        return students.some((student: any) => this.studentHasNoBus(student));
      case 'all':
      default:
        return true;
    }
  }

  /** Students considered for class/stats (respects selected class). */
  private scopedStudents(item: any): any[] {
    const linked = item?.students || [];
    if (!this.selectedClassId) {
      return linked;
    }
    return linked.filter(
      (student: any) => String(student?.class?.id || '') === this.selectedClassId,
    );
  }
  onPageSizeChange(size: string) { this.pageSize = Number(size); this.pageIndex = 0; }
  previousPage() { if (this.pageIndex > 0) this.pageIndex--; }
  nextPage() { if (this.pageIndex < this.totalPages - 1) this.pageIndex++; }
  isSelected(item: any) { return this.selected.some(s => s.id === item.id); }
  toggleSelection(item: any, event: Event) {
    event.stopPropagation();
    this.selected = this.isSelected(item)
      ? this.selected.filter(s => s.id !== item.id)
      : [...this.selected, item];
  }
  clearSelection() { this.selected = []; }

  openDetails(item: any) {
    if (!item?.id) {
      return;
    }
    this.router.navigate(portalCommands(this.router, 'settings', 'parents', item.id));
  }

  createContactGroup(entry?: any) {
    return new FormGroup({
      value: new FormControl(entry?.value || '', [Validators.required]),
      label: new FormControl(entry?.label || 'mobile'),
      isPrimary: new FormControl(!!entry?.isPrimary),
    });
  }

  createStudentLinkGroup(link?: any) {
    return new FormGroup({
      student: new FormControl(link?.id || link?.student || '', [Validators.required]),
      relation: new FormControl(link?.relation || 'father', [Validators.required]),
      isPrimaryEmergency: new FormControl(!!link?.isPrimaryEmergency),
    });
  }

  async setQrCode(value?: string) {
    this.qrCode = String(value || '').trim();
    if (!this.qrCode) {
      this.qrDataUrl = '';
      return;
    }
    try {
      this.qrDataUrl = await QRCode.toDataURL(this.qrCode, {
        width: 168,
        margin: 1,
        errorCorrectionLevel: 'M',
        color: { dark: '#173654', light: '#ffffff' },
      });
    } catch {
      this.qrDataUrl = '';
    }
  }

  async resetForm(item?: any) {
    this.form.reset({
      firstNameAr: item?.firstNameAr || '',
      lastNameAr: item?.lastNameAr || '',
      firstNameEn: item?.firstNameEn || '',
      lastNameEn: item?.lastNameEn || '',
    });
    this.phones.clear();
    this.emails.clear();
    this.studentLinks.clear();
    (item?.phones || []).forEach((p: any) => this.phones.push(this.createContactGroup(p)));
    (item?.emails || []).forEach((e: any) => this.emails.push(this.createContactGroup(e)));
    (item?.students || []).forEach((link: any) => this.studentLinks.push(this.createStudentLinkGroup(link)));
    await this.setQrCode(item?.qrCode);
  }

  async onAdd() {
    this.mode = 'add';
    this.selectedId = '';
    await this.resetForm();
    this.isError = false;
    this.open = true;
  }

  async onEdit() {
    const item = this.selected[0];
    if (!item?.id) {
      return;
    }
    await this.openEditById(item.id, item);
  }

  async openEditById(id: string, fallback?: any) {
    this.mode = 'edit';
    this.selectedId = id;
    this.isError = false;
    try {
      const fresh = await this.parentsService.getById(id);
      await this.resetForm(fresh);
      this.open = true;
    } catch {
      await this.resetForm(fallback || this.items.find(item => item.id === id));
      this.open = true;
    }
  }

  addPhone() { this.phones.push(this.createContactGroup()); }
  removePhone(i: number) { this.phones.removeAt(i); }
  addEmail() { this.emails.push(this.createContactGroup()); }
  removeEmail(i: number) { this.emails.removeAt(i); }
  addStudentLink() { this.studentLinks.push(this.createStudentLinkGroup()); }
  removeStudentLink(i: number) { this.studentLinks.removeAt(i); }

  onDeleteMany() {
    this.deleteModalBody = this.selected.length === 1 ? 'PARENTS.DELETE_ONE' : 'PARENTS.DELETE_MANY';
    this.deleteModalParams = this.selected.length === 1
      ? { name: this.displayName(this.selected[0]) }
      : { count: this.selected.length };
    this.showDeleteModal = true;
  }

  async onDeleteConfirm(confirmed: boolean) {
    if (confirmed) {
      for (const item of this.selected) {
        try {
          await this.parentsService.delete(item.id);
        } catch (error: any) {
          this.isError = true;
          this.errorMessage = error?.error?.errorMessages?.[0]?.message || 'PARENTS.DELETE_FAILED';
          this.showDeleteModal = false;
          await this.fetchItems();
          return;
        }
      }
      this.selected = [];
      await this.fetchItems();
    }
    this.showDeleteModal = false;
  }

  async submit() {
    if (this.form.invalid) {
      this.isError = true;
      this.errorMessage = 'COMMON.REQUIRED_FIELDS';
      return;
    }
    const raw = this.form.getRawValue();
    const body = {
      firstNameAr: raw.firstNameAr,
      lastNameAr: raw.lastNameAr,
      firstNameEn: raw.firstNameEn,
      lastNameEn: raw.lastNameEn,
      phones: (raw.phones || []).filter((p: any) => p.value),
      emails: (raw.emails || []).filter((e: any) => e.value),
      studentLinks: (raw.studentLinks || []).map((link: any) => ({
        student: link.student,
        relation: link.relation,
        isPrimaryEmergency: !!link.isPrimaryEmergency,
      })),
    };
    try {
      if (this.mode === 'edit') {
        await this.parentsService.update(this.selectedId, body);
      } else {
        await this.parentsService.create(body);
      }
      this.open = false;
      this.selected = [];
      await this.fetchItems();
    } catch (error: any) {
      this.isError = true;
      this.errorMessage = error?.error?.errorMessages?.[0]?.message || 'PARENTS.SAVE_FAILED';
    }
  }

  closeModal() {
    this.open = false;
    this.isError = false;
    this.qrCode = '';
    this.qrDataUrl = '';
  }

  async printCardsPdf(): Promise<void> {
    if (this.exporting) {
      return;
    }
    if (!this.selectedClassId) {
      this.isError = true;
      this.errorMessage = 'PARENTS.PDF_SELECT_CLASS';
      return;
    }

    const source = this.selected.length ? this.selected : this.filteredItems;
    const parents = source.filter(item => {
      const matching = this.matchingStudentsForPrint(item);
      if ((this.selectedClassId || this.printScope === 'no_bus') && !matching.length) {
        return false;
      }
      if (this.cardsParentScope === 'primary') {
        return matching.some((student: any) => !!student?.isPrimaryEmergency);
      }
      return true;
    });

    if (!parents.length) {
      this.isError = true;
      this.errorMessage = this.cardsParentScope === 'primary'
        ? 'PARENTS.CARDS_PDF_EMPTY_PRIMARY'
        : 'PARENTS.CARDS_PDF_EMPTY';
      return;
    }

    this.exportingCards = true;
    this.isError = false;
    this.errorMessage = '';
    try {
      const isArabic = this.languageService.current === 'ar';
      await this.parentCardsPdfService.export({
        schoolName: this.schoolName,
        isArabic,
        cutMarginMm: this.cutMarginMm,
        labels: {
          title: this.translate.instant('PARENTS.PDF_CARD_TITLE'),
          studentsLabel: this.translate.instant('PARENTS.STUDENTS'),
          noStudents: this.translate.instant('PARENTS.NO_STUDENTS'),
          noQr: this.translate.instant('PARENTS.PDF_NO_QR'),
        },
        parents: this.orderParentsBySharedStudents(parents, isArabic)
          .map(item => {
            const studentsSource = (item.students || [])
              .slice()
              .sort((a: any, b: any) =>
                this.studentDisplayName(a, isArabic)
                  .localeCompare(this.studentDisplayName(b, isArabic), isArabic ? 'ar' : 'en'),
              );
            const studentsText = studentsSource
              .map((student: any) => {
                const name = this.studentDisplayName(student, isArabic);
                const className = String(student?.class?.name || '').trim();
                return className ? `${name} (${className})` : name;
              })
              .filter(Boolean)
              .join('\n');
            const phones = item.phones || [];
            const phone = String(
              phones.find((entry: any) => entry.isPrimary)?.value
              || phones[0]?.value
              || '',
            ).trim();
            return {
              id: item.id,
              nameAr: `${item.firstNameAr || ''} ${item.lastNameAr || ''}`.trim(),
              nameEn: `${item.firstNameEn || ''} ${item.lastNameEn || ''}`.trim(),
              studentsText,
              phone,
              qrCode: String(item.qrCode || '').trim(),
            };
          }),
      });
    } catch {
      this.isError = true;
      this.errorMessage = 'PARENTS.CARDS_PDF_FAILED';
    } finally {
      this.exportingCards = false;
    }
  }

  async printListPdf(): Promise<void> {
    if (this.exporting) {
      return;
    }
    if (!this.selectedClassId) {
      this.isError = true;
      this.errorMessage = 'PARENTS.PDF_SELECT_CLASS';
      return;
    }

    const source = this.selected.length ? this.selected : this.filteredItems;
    const scoped = source.filter(item => this.matchingStudentsForPrint(item).length > 0);

    if (!scoped.length) {
      this.isError = true;
      this.errorMessage = 'PARENTS.LIST_PDF_EMPTY';
      return;
    }

    this.exportingList = true;
    this.isError = false;
    this.errorMessage = '';
    try {
      const isArabic = this.languageService.current === 'ar';
      const noBusLabel = this.translate.instant('PARENTS.LIST_PDF_NO_BUS');
      const rows = this.orderParentsBySharedStudents(scoped, isArabic)
        .map(item => this.toListPdfRow(item, isArabic, noBusLabel));

      await this.parentsListPdfService.export({
        schoolName: this.schoolName,
        isArabic,
        scopeText: this.printScopeText(),
        labels: {
          title: this.translate.instant('PARENTS.LIST_PDF_TITLE'),
          scopeLabel: this.translate.instant('PARENTS.LIST_PDF_SCOPE'),
          total: this.translate.instant('PARENTS.LIST_PDF_TOTAL'),
          empty: this.translate.instant('PARENTS.LIST_PDF_EMPTY'),
          index: '#',
          name: this.translate.instant('PARENTS.NAME'),
          phones: this.translate.instant('PARENTS.SECTION_PHONES'),
          students: this.translate.instant('PARENTS.STUDENTS'),
          classes: this.translate.instant('STUDENTS.CLASS'),
          relation: this.translate.instant('STUDENTS.RELATION'),
          bus: this.translate.instant('STUDENTS.BUS'),
          noBus: noBusLabel,
        },
        rows,
      });
    } catch {
      this.isError = true;
      this.errorMessage = 'PARENTS.LIST_PDF_FAILED';
    } finally {
      this.exportingList = false;
    }
  }

  private printScopeText(): string {
    const parts = [
      this.translate.instant(
        this.printScope === 'no_bus'
          ? 'PARENTS.PRINT_SCOPE_NO_BUS'
          : 'PARENTS.PRINT_SCOPE_ALL',
      ),
    ];
    if (this.selectedClassId) {
      const cls = this.classes.find(
        item => String(item.id || item._id || '') === this.selectedClassId,
      );
      parts.push(cls?.name || this.translate.instant('STUDENTS.CLASS'));
    } else {
      parts.push(this.allClassesLabel);
    }
    return parts.join(' · ');
  }

  private matchingStudentsForPrint(item: any): any[] {
    let linked = item?.students || [];
    if (this.selectedClassId) {
      linked = linked.filter(
        (student: any) => String(student?.class?.id || '') === this.selectedClassId,
      );
    }
    if (this.printScope === 'no_bus') {
      linked = linked.filter((student: any) => this.studentHasNoBus(student));
    }
    return linked;
  }

  private parentHasStudentInClass(item: any, classId: string): boolean {
    return (item?.students || []).some(
      (student: any) => String(student?.class?.id || '') === classId,
    );
  }

  private studentHasNoBus(student: any): boolean {
    return !student?.bus?.id && !student?.bus?.number && !student?.bus?.name;
  }

  private toListPdfRow(item: any, isArabic: boolean, noBusLabel: string) {
    const studentsForRow = this.matchingStudentsForPrint(item);

    return {
      name: `${item.firstNameAr || ''} ${item.lastNameAr || ''}`.trim()
        || `${item.firstNameEn || ''} ${item.lastNameEn || ''}`.trim(),
      phones: (item.phones || [])
        .map((phone: any) => String(phone.value || '').trim())
        .filter(Boolean)
        .join('\n'),
      students: studentsForRow
        .map((student: any) => this.studentDisplayName(student, isArabic))
        .filter(Boolean)
        .join('\n') || '—',
      classes: studentsForRow
        .map((student: any) => student?.class?.name || '—')
        .join('\n') || '—',
      relation: studentsForRow
        .map((student: any) => this.relationLabel(student?.relation))
        .join('\n') || '—',
      bus: studentsForRow
        .map((student: any) => student?.bus?.number || student?.bus?.name || noBusLabel)
        .join('\n') || '—',
    };
  }

  /**
   * Keep parents of the same student adjacent: walk students in name order,
   * then append each student's parents (father → mother → guardian) once.
   */
  private orderParentsBySharedStudents(parents: any[], isArabic: boolean): any[] {
    const byId = new Map<string, any>();
    for (const parent of parents) {
      if (parent?.id) {
        byId.set(String(parent.id), parent);
      }
    }

    const studentOrder: Array<{ id: string; name: string; parentIds: string[] }> = [];
    const studentMap = new Map<string, { id: string; name: string; parentIds: string[] }>();

    for (const parent of parents) {
      const linked = this.matchingStudentsForPrint(parent);
      const students = linked.length ? linked : (parent.students || []);
      for (const student of students) {
        const studentId = String(student?.id || '');
        if (!studentId) {
          continue;
        }
        let entry = studentMap.get(studentId);
        if (!entry) {
          entry = {
            id: studentId,
            name: this.studentDisplayName(student, isArabic),
            parentIds: [],
          };
          studentMap.set(studentId, entry);
          studentOrder.push(entry);
        }
        const parentId = String(parent.id || '');
        if (parentId && !entry.parentIds.includes(parentId)) {
          entry.parentIds.push(parentId);
        }
      }
    }

    studentOrder.sort((a, b) => a.name.localeCompare(b.name, isArabic ? 'ar' : 'en'));

    const ordered: any[] = [];
    const seen = new Set<string>();

    for (const student of studentOrder) {
      const studentParents = student.parentIds
        .map(id => byId.get(id))
        .filter(Boolean)
        .sort((a, b) => {
          const rel = this.relationSortRank(this.parentRelationForStudent(a, student.id))
            - this.relationSortRank(this.parentRelationForStudent(b, student.id));
          if (rel !== 0) {
            return rel;
          }
          return this.displayName(a).localeCompare(this.displayName(b), isArabic ? 'ar' : 'en');
        });

      for (const parent of studentParents) {
        const id = String(parent.id || '');
        if (!id || seen.has(id)) {
          continue;
        }
        seen.add(id);
        ordered.push(parent);
      }
    }

    for (const parent of parents) {
      const id = String(parent?.id || '');
      if (!id || seen.has(id)) {
        continue;
      }
      seen.add(id);
      ordered.push(parent);
    }

    return ordered;
  }

  private parentRelationForStudent(parent: any, studentId: string): string {
    const linked = [...(this.matchingStudentsForPrint(parent) || []), ...(parent?.students || [])];
    const match = linked.find((student: any) => String(student?.id || '') === studentId);
    return String(match?.relation || '').trim().toLowerCase();
  }

  private relationSortRank(relation: string): number {
    switch (relation) {
      case 'father':
        return 0;
      case 'mother':
        return 1;
      case 'guardian':
        return 2;
      default:
        return 3;
    }
  }

  private studentDisplayName(student: any, isArabic: boolean): string {
    const name = isArabic
      ? `${student?.firstNameAr || ''} ${student?.lastNameAr || ''}`.trim()
      : `${student?.firstNameEn || ''} ${student?.lastNameEn || ''}`.trim();
    return name || student?.studentCode || '—';
  }

  private relationLabel(relation?: string): string {
    const key = String(relation || '').trim().toUpperCase();
    if (!key) {
      return '—';
    }
    const translated = this.translate.instant(`STUDENTS.RELATION_${key}`);
    return translated.startsWith('STUDENTS.') ? relation || '—' : translated;
  }
}
